import { inject, Service, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { paramMapWithName } from './operators';

@Service()
export class RouterParamService {
  readonly #router = inject(Router);

  readonly #contentIdentifier = signal<string | null>(null);

  readonly contentIdentifier = this.#contentIdentifier.asReadonly();

  readonly #methodSlug = signal<string | null>(null);

  readonly methodSlug = this.#methodSlug.asReadonly();

  readonly #arrangementCode = signal<string | null>(null);

  readonly arrangementCode = this.#arrangementCode.asReadonly();

  constructor() {
    this.#setupRouteListener();
  }

  setQueryParam(name: string, value: string | null) {
    const queryParams = {
      ...this.#router.routerState.snapshot.root.queryParams,
    };

    if (value === null) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete queryParams[name];
    } else {
      queryParams[name] = value;
    }

    this.#router.navigate([], {
      queryParams,
    });
  }

  #setupRouteListener() {
    this.#router.events
      .pipe(
        takeUntilDestroyed(),
        filter((event) => event instanceof NavigationEnd),
      )
      .subscribe(() => {
        this.#resolveContentIdentifier();
        this.#resolveMethodSlug();
        this.#resolveArrangementCode();
      });
  }

  #resolveContentIdentifier() {
    const contentIdentifier = this.#resolveRouteParam(
      'contentIdentifier',
      true,
    );

    if (contentIdentifier !== this.#contentIdentifier()) {
      this.#contentIdentifier.set(contentIdentifier);
    }
  }

  #resolveMethodSlug() {
    const methodSlug = this.#resolveRouteParam('methodSlug');

    if (methodSlug !== this.#methodSlug()) {
      this.#methodSlug.set(methodSlug);
    }
  }

  #resolveArrangementCode() {
    const arrangementCode = this.#resolveRouteParam('arrangementCode');

    if (arrangementCode !== this.#arrangementCode()) {
      this.#arrangementCode.set(arrangementCode);
    }
  }

  #resolveRouteParam(param: string, queryParam = false): string | null {
    const paramMap = paramMapWithName(
      param,
      this.#router.routerState.root.children,
    );

    const foundParam = paramMap?.get(param) ?? null;

    if (queryParam && foundParam === null) {
      return this.#router.routerState.snapshot.root.queryParams[param] ?? null;
    }

    return foundParam;
  }
}
